import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api";
import { useUserFilter, userFilterSearch } from "@/lib/user-filter";
import { UserFilterChip } from "@/components/admin/user-filter-chip";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  History,
  ChevronLeft,
  ChevronRight,
  User as UserIcon,
  FileText,
  Award,
  FolderTree,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/admin/audit-log")({
  component: AuditLogPage,
  validateSearch: userFilterSearch,
});

interface AuditEvent {
  id: string;
  entityType: string;
  entityId: string;
  action: string;
  actorUserId: string;
  actorName: string | null;
  actorEmail: string | null;
  description: string;
  createdAt: string;
}

const ENTITY_ICONS: Record<string, typeof UserIcon> = {
  user: UserIcon,
  certification: Award,
  division: FolderTree,
  flha: FileText,
};

const ACTION_BADGE: Record<string, string> = {
  CREATE: "bg-green-500/15 text-green-700 dark:text-green-400",
  UPDATE: "bg-blue-500/15 text-blue-700 dark:text-blue-400",
  DELETE: "bg-red-500/15 text-red-700 dark:text-red-400",
  UPDATE_BLOCKED: "bg-red-500/15 text-red-700 dark:text-red-400",
  APPROVE: "bg-green-500/15 text-green-700 dark:text-green-400",
  REJECT: "bg-red-500/15 text-red-700 dark:text-red-400",
  SUBMIT: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-400",
  SIGN: "bg-purple-500/15 text-purple-700 dark:text-purple-400",
  ATTACH_FILE: "bg-gray-500/15 text-gray-700 dark:text-gray-400",
  PDF_GENERATED: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
};

const ACTION_LABEL: Record<string, string> = {
  CREATE: "Created",
  UPDATE: "Updated",
  DELETE: "Deleted",
  UPDATE_BLOCKED: "Deleted",
  APPROVE: "Approved",
  REJECT: "Rejected",
  SUBMIT: "Submitted",
  SIGN: "Signed",
  ATTACH_FILE: "Attached file",
  PDF_GENERATED: "PDF generated",
};

function AuditLogPage() {
  const userFilter = useUserFilter();
  const [entityType, setEntityType] = useState("all");
  const [page, setPage] = useState(1);
  const limit = 50;

  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("limit", String(limit));
  if (entityType !== "all") params.set("entityType", entityType);
  if (userFilter.userId) params.set("userId", userFilter.userId);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "audit-log", entityType, page, userFilter.userId],
    queryFn: () =>
      apiGet<{ data: AuditEvent[]; total: number; page: number; limit: number }>(
        `/api/audit?${params.toString()}`
      ),
  });

  const events = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4">
      {userFilter.userId && <UserFilterChip name={userFilter.userName} onClear={userFilter.clear} />}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Audit Log</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Append-only history of admin actions for accountability and investigations.</p>
        </div>
        <p className="text-xs text-muted-foreground shrink-0">
          {total} event{total !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        <Select
          value={entityType}
          onValueChange={(v) => {
            setEntityType(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All event types</SelectItem>
            <SelectItem value="user">Users</SelectItem>
            <SelectItem value="certification">Certifications</SelectItem>
            <SelectItem value="division">Divisions</SelectItem>
            <SelectItem value="flha">FLHAs</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : events.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <History className="h-8 w-8 text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">No audit events yet.</p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Events appear here as admins create, edit, or delete things.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {events.map((event) => {
            const Icon = ENTITY_ICONS[event.entityType] ?? FileText;
            const date = new Date(event.createdAt);
            return (
              <Card key={event.id}>
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-text-primary">
                          {event.actorName || event.actorEmail || "Unknown user"}
                        </span>
                        <Badge
                          className={cn(
                            "text-[10px] px-1.5 py-0",
                            ACTION_BADGE[event.action] ?? "bg-gray-500/15 text-gray-700"
                          )}
                        >
                          {ACTION_LABEL[event.action] ?? event.action}
                        </Badge>
                        <span className="text-xs text-muted-foreground capitalize">
                          {event.entityType}
                        </span>
                      </div>
                      <p className="text-sm text-text-primary mt-0.5">
                        {event.description || "—"}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        {date.toLocaleString("en-CA", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
